import { useEffect } from 'react';
import type { FC } from 'react';
import { Button } from '@components/Button/DefaultButton/Button';
import { Modal, ModalTitle, ModalBody, ModalContent, ModalFooter, BackDrop, Spinner, InfoIcon, CloseButton, CloseIcon } from './DefaultModal.styles';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { selectIsLoading, setRedirectImmediately } from '@/reducers/currentPage';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import SafeHTML from '@/utils/safeHTML';
import { replaceTemplateVariables } from '@/utils/replaceTemplateVariables';
import { theme } from '@/Theme';

export interface DefaultModalProps {
  openModal: boolean;
  setOpenModal: (isOpen: boolean) => void;
  typeModal?: 'submit' | 'default';
  infoModal?: { header?: string; body?: string; bodyLow?: string; bodyHigh?: string; button?: string; textOne?: string; textTwo?: string };
  isErrorModal?: boolean;
  flexDirectionContent?: 'row' | 'column';
  preventBackgroundInteraction?: boolean;
  showCloseButton?: boolean;
}
export const DefaultModal: FC<DefaultModalProps> = ({
  openModal,
  setOpenModal,
  infoModal,
  isErrorModal = false,
  flexDirectionContent,
  preventBackgroundInteraction = false,
  showCloseButton = false,
}) => {
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (preventBackgroundInteraction && isErrorModal) {
      dispatch(setRedirectImmediately(true));
    }
  }, [preventBackgroundInteraction]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeModal = () => {
    if (isErrorModal) templateConfig?.pages?.returnPage ? (window.location.href = templateConfig?.pages?.returnPage) : navigate('/');
    setOpenModal(false);
  };

  let buttonWithLender = replaceTemplateVariables(infoModal?.button, templateConfig);
  // Remove trailing slash from button text if it appears to be a URL
  if (buttonWithLender && (buttonWithLender.startsWith('http') || buttonWithLender.includes('www.'))) {
    buttonWithLender = buttonWithLender.replace(/\/+$/, '');
  }
  const headerWithLender = replaceTemplateVariables(infoModal?.header, templateConfig);
  const bodyWithLender = replaceTemplateVariables(infoModal?.body, templateConfig);

  const isLoading = useAppSelector(selectIsLoading);
  const isSubmit = true;

  useEffect(() => {
    if (openModal && isErrorModal) {
      const isBlocklistModal = headerWithLender?.includes('Unable to Process') || headerWithLender?.includes("We're Unable");
      if (isBlocklistModal) {
        setTimeout(() => {
          const modalTitle = document.querySelector('[data-testid="modal-title"]') as HTMLElement;
          if (modalTitle) {
            const primaryColor = templateConfig?.globalTheme?.primaryColor || theme.colors.textPrimaryLd;
            const computedStyle = window.getComputedStyle(modalTitle);
            const expectedRgb = primaryColor.startsWith('#')
              ? `rgb(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)})`
              : primaryColor;
            if (computedStyle.color !== expectedRgb && isErrorModal) {
              modalTitle.style.setProperty('color', primaryColor, 'important');
            }
          }
        }, 100);
      }
    }
  }, [openModal, isErrorModal, headerWithLender, templateConfig]);

  return createPortal(
    <>
      <BackDrop
        $isOpen={openModal}
        $preventBackgroundInteraction={preventBackgroundInteraction}
        onClick={preventBackgroundInteraction ? undefined : closeModal}
        data-testid="modal-backdrop"
      />
      {openModal && (
        <Modal $isLoading={isLoading} $isOpen={openModal} $preventBackgroundInteraction={preventBackgroundInteraction}>
          {isLoading && isSubmit ? (
            <ModalBody>
              <Spinner /> <ModalTitle $isLoading={isLoading}>{infoModal?.textOne}</ModalTitle> <ModalContent $isLoading={isLoading}>{infoModal?.textTwo}</ModalContent>
            </ModalBody>
          ) : (
            <ModalBody>
              {showCloseButton && (
                <CloseButton type="button" aria-label="Close modal" onClick={closeModal} data-testid="modal-close-button">
                  <CloseIcon aria-hidden />
                </CloseButton>
              )}
              <InfoIcon />
              <ModalTitle
                $isLoading={isLoading}
                $isErrorModal={isErrorModal}
                data-testid="modal-title"
                style={isErrorModal ? { color: templateConfig?.globalTheme?.primaryColor || theme.colors.textPrimaryLd } : undefined}
              >
                {headerWithLender}
              </ModalTitle>{' '}
              <ModalContent $isLoading={isLoading} $flexDirectionContent={flexDirectionContent}>
                <SafeHTML tag="p" html={bodyWithLender || ''} />
              </ModalContent>
              {infoModal?.button && (
                <ModalFooter>
                  <Button label={buttonWithLender} onClick={closeModal} variant="secondary" buttonSize="l" />{' '}
                </ModalFooter>
              )}
            </ModalBody>
          )}
        </Modal>
      )}
    </>,
    document.body,
  );
};
